<script setup lang="ts">
import { explainMonsterAffix } from '@/core/monster/affix'
import { useUserStore } from '@/stores/modules/user'
import type { PersonMonsterType } from '@/types/monster'
import { socketEmit, socketEmitAsync } from '@/utils/web-socket'
import { dayjs } from 'element-plus'
import { ref } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import BoundlessTowerRank from './BoundlessTowerRank.vue'
import { watch } from 'vue'
import { useCommonStore } from '@/stores/modules/common'

const commonStore = useCommonStore()
const userStore = useUserStore()

defineProps<{ monster?: PersonMonsterType }>()
const emits = defineEmits(['start', 'sweep'])

function handleStartBoundlessTower(isAuto = false) {
  if (userStore.boundlessTowerTimes <= 0) return ElMessage.warning('本层挑战次数已耗尽')
  const curTime = dayjs()
  const week = curTime.day()
  if (week === 6) {
    const hour = curTime.hour()
    if (hour === 23) {
      const min = curTime.minute()
      if (min >= 55) return ElMessage.warning('无垠之塔结算中，无法进入挑战')
    }
  }

  ElMessageBox.confirm(
    `<p class="affix">确定进入【无垠之塔】挑战吗？（不论成败都将<span class="shrink">消耗</span>挑战次数）${
      userStore.boundlessTowerTimes < 3 ? '<br /><b>【若消耗所有挑战次数，则本周无法再进行无垠之塔挑战】</b>' : ''
    }${isAuto ? '<br />您选择了【自动】挑战，将自动挑战至失败为止' : ''}</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await socketEmitAsync<PersonMonsterType>({ type: 'startBoundlessTower', data: { isAuto } })
      userStore.boundlessTowerTimes--
      emits('start')
    })
    .catch(() => {})
}

const sweepLevel = ref(0)
function getSweepLevel() {
  socketEmit<number>({
    type: 'getSweepBoundlessTowerLevel',
    callBack: (res) => {
      sweepLevel.value = res.data
    },
  })
}
function handleSweepBoundlessTower() {
  ElMessageBox.confirm(
    `<p class="affix">确定<span class="info">扫荡</span>无垠之塔吗？（根据上周通关层数，本周您可扫荡至 <span class="grow">${
      sweepLevel.value + 1
    }</span>层）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await socketEmitAsync({ type: 'sweepBoundlessTower' })
      userStore.boundlessTowerTimes = 10
      userStore.boundlessTowerLevel = sweepLevel.value
      ElMessage.success('扫荡成功，请在邮件处领取奖励')
      useCommonStore().getMailList()
      sweepLevel.value = 0
      emits('sweep')
    })
    .catch(() => {})
}
function handleSecondAwakenSweepBoundlessTower() {
  if (userStore.boundlessTowerLevel >= 50) return ElMessage.warning('已超出可扫荡的最高层级')
  ElMessageBox.confirm(`<p class="affix">确定<span class="info">扫荡</span>无垠之塔吗？</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync({ type: 'secondAwakenSweepBoundlessTower' })
      ElMessage.success('扫荡成功，请在邮件处领取奖励')
      userStore.boundlessTowerTimes = 10
      userStore.boundlessTowerLevel = 50
      useCommonStore().getMailList()
      emits('sweep')
    })
    .catch(() => {})
}
const rankShow = ref(false)

watch(
  () => commonStore.isSocketInit,
  (val) => {
    if (!val || userStore.advanceLevel !== 4) return
    getSweepLevel()
  },
)
</script>

<template>
  <div class="border-wrap boundless-tower">
    <h4 class="supreme-simple">
      无垠之塔
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>无垠之塔说明</h4>
            <p>1. 无垠之塔<span class="info">每周日0点刷新</span>，每次刷新塔内都将被随机的怪物占领，同时怪物的各项数值也是随机的。</p>
            <p>2. 每层仅有<span class="info">10次</span>挑战机会，进入则扣除次数（无论成败），若无次数则本周无法再挑战。</p>
            <p>3. 您需要在<span class="info">3分钟</span>的时间内通过挑战，进入后切装无效，请提前根据怪物属性搭配合理的装备。</p>
            <p>4. 奖励规则：</p>
            <p style="text-indent: 2em">
              a.
              <span class="gold">金币</span>奖励：1~10层每层奖励 300,000金币，11~30层每层奖励 400,000金币，而后每层奖励 150,000金币。
            </p>
            <p style="text-indent: 2em">
              b. <span class="unique">材料</span>奖励：1~20层，将固定奖励 20~30<span class="supreme-simple">至臻精造石</span>，而后每层奖励 1~15<span
                class="supreme-simple"
                >至臻精造石</span
              >。
            </p>
            <p style="text-indent: 2em">
              c. <span class="antiquity">特殊</span>奖励：5层，固定奖励 1 <span class="supreme-simple">至臻</span>装备；10、15、20层，奖励 100<span
                class="supreme-simple"
                >至臻精造石</span
              >，1<span class="mp">技能升级石</span>。
            </p>
            <p style="text-indent: 2em">
              d. <span class="info">随机</span>奖励：每层都有概率触发一次随机奖励：<span class="info">2%</span>概率 1
              <span class="supreme-simple">至臻</span>装备，<span class="info">2%</span>概率 1<span class="grow">至臻刻印券</span>；<span class="info"
                >2%</span
              >概率 1<span class="mp">技能书</span>。
            </p>
          </div>
        </el-popover>
      </div>
    </h4>
    <p>
      <span style="margin-right: 15px">当前层级：{{ userStore.boundlessTowerLevel + 1 }}</span>
      <span>剩余次数：{{ userStore.boundlessTowerTimes }}</span>
    </p>
    <p>
      当前怪物：
      <el-popover placement="bottom" :width="300" trigger="click" popper-class="monster-detail" :teleported="false" :disabled="!monster">
        <template #reference>
          <el-button size="small" type="danger" plain>
            <span v-show="monster">{{ monster?.name }}</span>
            <span v-show="!monster">暂无战斗</span>
          </el-button>
        </template>
        <NewMonsterDetail :monster="monster" />
      </el-popover>
    </p>
    <div style="margin: 10px 0">
      <el-button type="success" plain @click="handleStartBoundlessTower(false)" :disabled="userStore.advanceLevel !== 4">
        {{ userStore.advanceLevel !== 4 ? '进阶后解锁' : '进入' }}
      </el-button>
      <el-button color="#ffb3a7" :dark="commonStore.isDarkMode" plain @click="handleStartBoundlessTower(true)" :disabled="!userStore.soulType"
        >自动</el-button
      >
      <el-button type="primary" plain @click="rankShow = true">排行</el-button>
      <el-button color="#f7c99c" :dark="commonStore.isDarkMode" plain @click="handleSweepBoundlessTower">扫荡</el-button>
    </div>
  </div>

  <el-dialog v-model="rankShow" width="100%" style="max-width: 500px">
    <BoundlessTowerRank />
    <template #footer>
      <p style="text-align: center">
        <el-button plain @click="rankShow = false" size="small">关闭</el-button>
      </p>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.boundless-tower {
  flex-flow: column;
}
</style>
